import { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

import { useSelector } from 'react-redux';
import {
  selectIsAdmin,
  selectUserInfo,
  selectUserToken,
} from './redux/features/user/userSelectors';
import { useGetUserInfoMutation } from './redux/services/user/userApi';
import { tokenSubject } from './redux/services/api';

import { Loader } from './components/ui/Loader';
import { ToasterComponent } from './components/ui/ToasterComponent';
import { NavMobile } from './layouts/NavMobile';
import { SideBar } from './layouts/SideBar';
import { NotFound } from './pages/NotFound';

import { sideLinksAuthorised, sideLinksNonAuthorised } from './data/sidebar';
import { routesAuthorised, routesNonAuthorised } from './routes';
import { handleOverflowHiddenToBody } from './utils/ui';

import './index.css';
import { MapPage } from './pages/MapPage';

function App() {
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sideLinks, setSideLinks] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const userToken = useSelector(selectUserToken);
  const userInfo = useSelector(selectUserInfo);
  const isAdmin = useSelector(selectIsAdmin);

  const [getUserInfo] = useGetUserInfoMutation();

  // TEMPORARY: treat "mobile viewport" as PWA for testing until we split
  // the mobile-viewport concern from the actual PWA-standalone concern in
  // the isPwa consumers (SideBar, RealtyList, CardRealty, CardRealtyPhotos).
  // See .claude/plans/map-pwa-refactor.md.
  const isPwa =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(max-width: 767px)').matches;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    handleOverflowHiddenToBody(!isSidebarOpen);
  };

  const toggleSidebarClose = () => {
    setIsSidebarOpen(false);
    handleOverflowHiddenToBody(false);
  };

  const setRouting = async () => {
    setIsLoading(true);

    if (userToken) {
      await setSideLinks(sideLinksAuthorised);
      await setRoutes(routesAuthorised);
    } else {
      await setSideLinks(sideLinksNonAuthorised);
      await setRoutes(routesNonAuthorised);
    }

    setIsLoading(false);
  };

  // Keyed on the agent, not the token, so a silent refresh refetches nothing.
  const agentId = tokenSubject(userToken);
  const isLoggedIn = !!userToken;

  useEffect(() => {
    if (userToken) {
      getUserInfo(userToken);
    }
  }, [agentId]);

  useEffect(() => {
    setRouting();
  }, [isLoggedIn]);

  // Don't show a bfcache page from before logout or an account switch.
  useEffect(() => {
    const reloadIfRestored = event => {
      if (event.persisted) window.location.reload();
    };

    window.addEventListener('pageshow', reloadIfRestored);
    return () => window.removeEventListener('pageshow', reloadIfRestored);
  }, []);

  return (
    <>
      <ToasterComponent />

      <SideBar
        isSidebarOpen={isSidebarOpen}
        toggleSidebarClose={toggleSidebarClose}
        sideLinks={sideLinks}
        isPwa={isPwa}
      />

      <div className="flex flex-col md:flex-row">
        <NavMobile
          toggleSidebarClose={toggleSidebarClose}
          toggleSidebar={toggleSidebar}
        />

        {isLoading ? (
          <div className="flex flex-col justify-center items-center gap-5 pt-[4.4rem] pb-6 pl-2 pr-2 w-full h-screen bg-white md:pt-6 md:pr-5 md:pl-[11.5rem] lg:pl-[12.25rem]">
            <Loader
              height={40}
              width={40}
            />
          </div>
        ) : (
          <Routes>
            {routes.map(route => {
              const { id, path, element, isAuthorised, adminOnly } = route;

              // Hide only once we know the user isn't admin; while userInfo
              // loads the route stays so deep links don't flash 404.
              if (adminOnly && userInfo && !isAdmin) return null;

              if (
                (userToken && isAuthorised) ||
                (!userToken && !isAuthorised)
              ) {
                return (
                  <Route
                    key={id}
                    path={path}
                    element={element}
                  />
                );
              }
            })}

            {userToken && (
              <Route
                path={'/map'}
                element={<MapPage />}
              />
            )}

            <Route
              path="*"
              element={<NotFound />}
            />
          </Routes>
        )}
      </div>
    </>
  );
}

export default App;
